import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronRight, Shield, Mail, Phone, Globe, ArrowLeft, Lock, Eye, Database, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LAST_UPDATE = "10 de abril de 2026";

function LegalHeader({ company }) {
  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a href="/superpoderes" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft size={18} className="text-gray-600" />
          </a>
          <div className="w-8 h-8 rounded-lg bg-red-600 flex items-center justify-center">
            <Shield size={15} className="text-white" />
          </div>
          <span className="font-semibold text-gray-900">{company?.app_name || "OkBot"}</span>
        </div>
        <nav className="hidden md:flex items-center gap-6 text-sm">
          <a href="/terminos" className="text-gray-500 hover:text-gray-900 transition-colors">Términos</a>
          <a href="/privacidad" className="text-red-600 font-medium border-b-2 border-red-600 pb-0.5">Privacidad</a>
          <a href="/aviso-legal" className="text-gray-500 hover:text-gray-900 transition-colors">Aviso Legal</a>
          <a href="/cookies" className="text-gray-500 hover:text-gray-900 transition-colors">Cookies</a>
        </nav>
      </div>
    </header>
  );
}

function Section({ number, icon, title, children }) {
  return (
    <section className="mb-10">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600">{icon}</div>
        <div>
          <span className="text-xs text-gray-400 uppercase tracking-wider">Sección {number}</span>
          <h2 className="text-xl font-semibold text-gray-900 leading-tight">{title}</h2>
        </div>
      </div>
      <div className="ml-13 space-y-3 text-gray-600 leading-relaxed pl-1">{children}</div>
    </section>
  );
}

export default function PrivacyPage() {
  const [company, setCompany] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/legal/public-info`).then(r => setCompany(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const c = company || {};
  const companyName = c.company_name || "OkBot";
  const email = c.email || "hola@okgo.mx";
  const privacyEmail = c.privacy_email || "privacidad@okgo.mx";
  const website = c.website || "https://okbot.okgo.mx";
  const appName = c.app_name || "OkBot";
  const city = c.city || "Ciudad de México";

  return (
    <div className="min-h-screen bg-gray-50">
      <LegalHeader company={company} />

      {/* Hero */}
      <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 text-orange-200 text-sm mb-4">
            <a href="/superpoderes" className="hover:text-white">Inicio</a>
            <ChevronRight size={14} />
            <span>Política de Privacidad</span>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <Shield size={36} className="text-orange-200" />
            <div>
              <h1 className="text-4xl font-bold">Política de Privacidad</h1>
              <p className="text-orange-100 mt-1">Cómo recopilamos, usamos y protegemos tus datos personales</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 mt-8 pt-8 border-t border-orange-400/40 text-sm text-orange-100">
            <div><span className="text-orange-300 block text-xs uppercase tracking-wide mb-1">Última actualización</span>{LAST_UPDATE}</div>
            <div><span className="text-orange-300 block text-xs uppercase tracking-wide mb-1">Marco legal</span>LFPDPPP</div>
            <div><span className="text-orange-300 block text-xs uppercase tracking-wide mb-1">Responsable</span>{companyName}</div>
            <div><span className="text-orange-300 block text-xs uppercase tracking-wide mb-1">Contacto DPO</span>{privacyEmail}</div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10">

          {/* Responsable */}
          <div className="mb-10 p-6 bg-orange-50 border border-orange-100 rounded-xl">
            <div className="flex items-center gap-2 mb-4">
              <UserCheck size={18} className="text-orange-600" />
              <h2 className="font-semibold text-gray-900">Responsable del Tratamiento</h2>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-orange-100">
                {[
                  ["Responsable", companyName],
                  ["Contacto general", email],
                  ["Contacto de privacidad", privacyEmail],
                  ["Sitio web", website],
                  ...(c.address ? [["Domicilio", [c.address, city, c.state, "México"].filter(Boolean).join(", ")]] : []),
                ].map(([k, v]) => (
                  <tr key={k}>
                    <td className="py-2.5 pr-6 font-medium text-gray-700 w-48">{k}</td>
                    <td className="py-2.5 text-gray-600">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Section number="1" icon={<Database size={18} />} title="Datos Personales que Recopilamos">
            <p>De conformidad con la <strong>Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)</strong>, {companyName} recopila las siguientes categorías de datos personales:</p>
            <table className="w-full mt-4 border border-gray-200 rounded-lg overflow-hidden text-sm">
              <thead className="bg-gray-50"><tr>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Categoría</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Datos específicos</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Obligatorio</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ["Identificación", "Nombre(s), apellido(s), OkUser (nickname)", "Sí"],
                  ["Contacto", "Correo electrónico, número de WhatsApp", "No"],
                  ["Autenticación", "PIN (almacenado en hash irreversible)", "Sí"],
                  ["Uso del servicio", "Módulos utilizados, historial de conversaciones con IA, preferencias", "Automático"],
                  ["Técnicos", "Dirección IP, navegador, sistema operativo, cookies de sesión", "Automático"],
                  ["Financieros", "Datos de tarjeta (procesados por Stripe, no almacenados por nosotros)", "Según plan"],
                ].map(([cat, datos, req]) => (
                  <tr key={cat} className="border-b border-gray-100">
                    <td className="py-3 px-4 font-medium text-gray-900">{cat}</td>
                    <td className="py-3 px-4 text-gray-600 text-sm">{datos}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${req === "Sí" ? "bg-red-100 text-red-700" : req === "No" ? "bg-gray-100 text-gray-600" : "bg-orange-100 text-orange-700"}`}>{req}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-4 text-sm bg-amber-50 border border-amber-200 rounded-lg p-4">
              <strong>Nota sobre datos sensibles:</strong> {appName} no recopila datos personales sensibles en los términos del artículo 3, fracción VI de la LFPDPPP, tales como origen étnico, estado de salud, información genética, creencias religiosas o datos biométricos.
            </p>
          </Section>

          <Section number="2" icon={<Eye size={18} />} title="Finalidades del Tratamiento">
            <p>Los datos personales recopilados serán utilizados para las siguientes finalidades:</p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              {[
                { title: "Primarias (necesarias)", items: ["Crear y gestionar tu cuenta de usuario", "Autenticarte mediante PIN", "Proveer los servicios contratados", "Procesar pagos y facturación", "Brindarte soporte técnico y atención"], color: "red" },
                { title: "Secundarias (opcionales)", items: ["Enviarte comunicaciones promocionales y novedades", "Realizar análisis estadísticos anónimos de uso", "Personalizar la experiencia del servicio", "Investigación y desarrollo de nuevas funcionalidades"], color: "orange" },
              ].map(({ title, items, color }) => (
                <div key={title} className={`p-5 bg-${color}-50 border border-${color}-100 rounded-xl`}>
                  <h3 className={`font-semibold text-${color}-900 mb-3 text-sm`}>{title}</h3>
                  <ul className="space-y-2">
                    {items.map(item => (
                      <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className={`w-4 h-4 rounded-full bg-${color}-600 text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5`}>✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm">Puedes <strong>oponerte al tratamiento</strong> para finalidades secundarias en cualquier momento enviando tu solicitud a {privacyEmail}, sin que ello afecte el acceso al Servicio.</p>
          </Section>

          <Section number="3" icon={<Lock size={18} />} title="Base Legal del Tratamiento">
            <table className="w-full border border-gray-200 rounded-lg overflow-hidden text-sm mt-2">
              <thead className="bg-gray-50"><tr>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Finalidad</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Base legal (LFPDPPP)</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ["Gestión de cuenta y prestación del servicio", "Ejecución de contrato (Art. 8)"],
                  ["Seguridad y prevención de fraudes", "Interés legítimo (Art. 18)"],
                  ["Comunicaciones comerciales", "Consentimiento del titular (Art. 8)"],
                  ["Cumplimiento de obligaciones legales", "Obligación legal (Art. 18)"],
                  ["Análisis estadísticos (datos anonimizados)", "Interés legítimo (Art. 18)"],
                ].map(([fin, base]) => (
                  <tr key={fin} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-gray-700">{fin}</td>
                    <td className="py-3 px-4 font-medium text-gray-900">{base}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section number="4" icon={<UserCheck size={18} />} title="Derechos ARCO">
            <p>De conformidad con los artículos 22 al 35 de la LFPDPPP, tienes los siguientes derechos respecto a tus datos personales:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              {[
                { letra: "A", nombre: "Acceso", desc: "Conocer qué datos tenemos de ti y cómo los usamos" },
                { letra: "R", nombre: "Rectificación", desc: "Corregir datos inexactos o incompletos" },
                { letra: "C", nombre: "Cancelación", desc: "Solicitar la eliminación de tus datos" },
                { letra: "O", nombre: "Oposición", desc: "Oponerte al tratamiento de tus datos" },
              ].map(({ letra, nombre, desc }) => (
                <div key={letra} className="p-5 border border-gray-200 rounded-xl text-center hover:border-red-300 hover:bg-red-50 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-red-600 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-3">{letra}</div>
                  <p className="font-semibold text-gray-900 text-sm mb-1">{nombre}</p>
                  <p className="text-xs text-gray-500">{desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 p-5 bg-gray-50 border border-gray-200 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-3">Cómo ejercer tus derechos ARCO</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span className="w-5 h-5 bg-red-600 text-white rounded-full text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>Envía tu solicitud a <strong className="text-gray-900">{privacyEmail}</strong> con asunto "Ejercicio de Derechos ARCO"</li>
                <li className="flex items-start gap-2"><span className="w-5 h-5 bg-red-600 text-white rounded-full text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>Incluye copia de identificación oficial y descripción detallada del derecho a ejercer</li>
                <li className="flex items-start gap-2"><span className="w-5 h-5 bg-red-600 text-white rounded-full text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>Recibirás respuesta en un plazo máximo de <strong className="text-gray-900">20 días hábiles</strong></li>
                <li className="flex items-start gap-2"><span className="w-5 h-5 bg-red-600 text-white rounded-full text-xs flex items-center justify-center flex-shrink-0 mt-0.5">4</span>Si no estás satisfecho, puedes recurrir al <strong className="text-gray-900">INAI (inai.org.mx)</strong></li>
              </ol>
            </div>
          </Section>

          <Section number="5" icon={<Shield size={18} />} title="Transferencias de Datos">
            <p>Para la prestación del Servicio, {companyName} puede compartir datos personales con las siguientes categorías de terceros:</p>
            <table className="w-full border border-gray-200 rounded-lg overflow-hidden text-sm mt-3">
              <thead className="bg-gray-50"><tr>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Tercero</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Finalidad</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Consentimiento</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ["Proveedores de infraestructura (cloud)", "Almacenamiento y procesamiento de datos", "No requerido (servicio necesario)"],
                  ["Stripe Inc.", "Procesamiento de pagos", "No requerido (ejecución de contrato)"],
                  ["Proveedores de IA (OpenRouter, etc.)", "Generación de respuestas de IA", "No requerido (servicio necesario)"],
                  ["RapidAPI Inc.", "Acceso a APIs de terceros", "No requerido (servicio necesario)"],
                  ["Autoridades gubernamentales", "Cumplimiento de obligaciones legales", "No requerido (obligación legal)"],
                ].map(([t, f, c]) => (
                  <tr key={t} className="border-b border-gray-100">
                    <td className="py-3 px-4 font-medium text-gray-900">{t}</td>
                    <td className="py-3 px-4 text-gray-600">{f}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section number="6" icon={<Lock size={18} />} title="Seguridad de los Datos">
            <p>Implementamos medidas de seguridad técnicas, administrativas y físicas conforme al artículo 19 de la LFPDPPP, incluyendo:</p>
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              {[
                { title: "Técnicas", items: ["Cifrado TLS en tránsito", "Hash bcrypt para PINs", "Autenticación por token JWT", "Bases de datos en entornos seguros"] },
                { title: "Administrativas", items: ["Acceso restringido a datos personales", "Políticas internas de privacidad", "Capacitación del personal", "Acuerdos de confidencialidad"] },
                { title: "Físicas", items: ["Infraestructura en centros de datos certificados", "Control de acceso físico", "Respaldos periódicos cifrados"] },
              ].map(({ title, items }) => (
                <div key={title} className="p-4 border border-gray-200 rounded-xl">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">{title}</h4>
                  <ul className="space-y-1.5">
                    {items.map(i => <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5"><span className="text-green-500 mt-0.5">✓</span>{i}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </Section>

          <Section number="7" icon={<Database size={18} />} title="Conservación de Datos">
            <table className="w-full border border-gray-200 rounded-lg overflow-hidden text-sm">
              <thead className="bg-gray-50"><tr>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Categoría de datos</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Período de conservación</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ["Datos de cuenta activa", "Durante la vigencia de la cuenta"],
                  ["Datos tras cancelación", "3 años (obligaciones fiscales y legales)"],
                  ["Historial de conversaciones con IA", "180 días después del último acceso"],
                  ["Registros de seguridad (logs)", "90 días"],
                  ["Datos de pagos", "5 años (SAT y Ley de IVA)"],
                ].map(([cat, per]) => (
                  <tr key={cat} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-gray-700">{cat}</td>
                    <td className="py-3 px-4 font-medium text-gray-900">{per}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section number="8" icon={<Mail size={18} />} title="Contacto y Quejas">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-gray-50 border border-gray-200 rounded-xl">
                <h3 className="font-semibold text-gray-900 mb-3">Contacto de Privacidad</h3>
                <div className="space-y-3 text-sm">
                  <a href={`mailto:${privacyEmail}`} className="flex items-center gap-2 text-red-600 hover:text-red-700">
                    <Mail size={16} />{privacyEmail}
                  </a>
                  <p className="text-gray-600">Tiempo de respuesta: <strong>20 días hábiles</strong></p>
                  <p className="text-gray-600">Horario: Lun–Vie, 9:00–18:00 (CST)</p>
                </div>
              </div>
              <div className="p-6 bg-red-50 border border-red-100 rounded-xl">
                <h3 className="font-semibold text-gray-900 mb-3">INAI — Autoridad Supervisora</h3>
                <p className="text-sm text-gray-600 mb-3">Si no estás satisfecho con nuestra respuesta, puedes presentar una queja ante el Instituto Nacional de Transparencia, Acceso a la Información y Protección de Datos Personales.</p>
                <a href="https://www.inai.org.mx" target="_blank" rel="noopener noreferrer"
                  className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1">
                  <Globe size={14} />inai.org.mx
                </a>
              </div>
            </div>
          </Section>

        </div>
      </div>

      <footer className="bg-gray-900 text-gray-400 py-10">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm">© {new Date().getFullYear()} {companyName}. Todos los derechos reservados.</p>
          <div className="flex gap-6 text-sm">
            <a href="/terminos" className="hover:text-gray-200 transition-colors">Términos</a>
            <a href="/privacidad" className="text-red-400 hover:text-red-300 transition-colors">Privacidad</a>
            <a href="/aviso-legal" className="hover:text-gray-200 transition-colors">Aviso Legal</a>
            <a href="/cookies" className="hover:text-gray-200 transition-colors">Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
